/**
 * The kvittering for one payment of share capital.
 *
 * Printed rather than rendered to PDF, portalled out of the app, and offered in
 * Norwegian or English behind the flag — all three explained on `PrintSheet`,
 * which this shares with the aksjeeierbevis.
 *
 * What is specific to this document is what it is *for*: the tegningskurs and
 * the innbetalingsdato are the holder's inngangsverdi, so those two are the
 * figures it exists to state and the two it emphasises.
 */
import { useState } from 'react'
import { fmtDateAbs, formatDate } from '@/shared/util/dates'
import { fmtKrExact, fmtKrPrice } from '@/shared/util/money'
import { SHARE_CLASS, type DocLang, type Receipt } from '@/types/shareDocuments'
import { Field, Gaps, PrintSheet } from './PrintSheet'

const COPY = {
  nb: {
    heading: 'Kvittering for innbetalt aksjekapital',
    headingExercise: 'Kvittering for innbetalt aksjekapital ved innløsning av opsjoner',
    orgNo: 'Org.nr.',
    holder: 'Aksjonær',
    name: 'Navn',
    address: 'Adresse',
    idCompany: 'Organisasjonsnummer',
    idPerson: 'Fødselsnummer',
    shares: 'Aksjene',
    count: 'Antall aksjer',
    shareClass: 'Aksjeklasse',
    par: 'Pålydende per aksje',
    premium: 'Overkurs per aksje',
    price: 'Tegningskurs per aksje',
    total: 'Totalt innbetalt',
    paidDate: 'Innbetalingsdato',
    increase: 'Kapitalforhøyelsen',
    round: 'Emisjon',
    roundDate: 'Emisjonsdato',
    resolution: 'Beslutning',
    note: 'Merknad',
    foot: 'Tegningskursen og innbetalingsdatoen ovenfor utgjør aksjonærens inngangsverdi på aksjene.',
    issued: 'utstedt',
  },
  en: {
    heading: 'Receipt for share capital paid',
    headingExercise: 'Receipt for share capital paid on exercise of options',
    orgNo: 'Reg. no.',
    holder: 'Shareholder',
    name: 'Name',
    address: 'Address',
    idCompany: 'Organisation number',
    idPerson: 'National identity number',
    shares: 'The shares',
    count: 'Number of shares',
    shareClass: 'Share class',
    par: 'Par value per share',
    premium: 'Share premium per share',
    price: 'Subscription price per share',
    total: 'Total paid',
    paidDate: 'Date of payment',
    increase: 'The capital increase',
    round: 'Issue',
    roundDate: 'Date of issue',
    resolution: 'Resolution',
    note: 'Note',
    foot: 'The subscription price and payment date above constitute the shareholder’s cost base (inngangsverdi) for these shares.',
    issued: 'issued',
  },
} satisfies Record<DocLang, Record<string, string>>

const nb = (n: number, lang: DocLang) => n.toLocaleString(lang === 'nb' ? 'nb-NO' : 'en-GB')

/**
 * Norwegian writes the document date `03.09.2026`; English writes it
 * `03 Sep 2026`.
 *
 * Not a style preference. `03.09.2026` is read as the third of September by
 * this document's Norwegian readers and as nothing at all by half its English
 * ones, and on a document whose whole purpose is to fix a date for a tax
 * authority, an ambiguous one is a defect. Both formatters already exist.
 */
const date = (iso: string, lang: DocLang) => (lang === 'nb' ? fmtDateAbs(iso) : formatDate(iso))

interface Props {
  receipt: Receipt
  /** ISO. The day the document itself is produced, for the closing line. */
  issuedOn: string
  onClose: () => void
}

export function ShareReceipt({ receipt: r, issuedOn, onClose }: Props) {
  const [lang, setLang] = useState<DocLang>('nb')
  const t = COPY[lang]
  const heading = r.kind === 'exercise' ? t.headingExercise : t.heading

  return (
    <PrintSheet label={heading} lang={lang} onLang={setLang} onClose={onClose}>
      <Gaps missing={r.missing} lang={lang} />

      <header className="kvitt__head">
        <div className="kvitt__co">
          <strong>{r.companyName || '—'}</strong>
          <span>
            {t.orgNo} {r.companyOrgNumber || '—'}
          </span>
        </div>
        <h1 className="kvitt__title">{heading}</h1>
      </header>

      <section className="kvitt__block">
        <div className="kvitt__blocktitle">{t.holder}</div>
        <dl className="kvitt__fields">
          <Field label={t.name}>{r.holderName || '—'}</Field>
          <Field label={t.address}>{r.holderAddress || '—'}</Field>
          <Field label={r.holderIsCompany ? t.idCompany : t.idPerson}>
            {r.holderIdNumber || '—'}
          </Field>
        </dl>
      </section>

      <section className="kvitt__block">
        <div className="kvitt__blocktitle">{t.shares}</div>
        <dl className="kvitt__fields">
          <Field label={t.count}>{nb(r.shares, lang)}</Field>
          <Field label={t.shareClass}>{SHARE_CLASS[lang]}</Field>
          <Field label={t.par}>{r.parValue == null ? '—' : fmtKrPrice(r.parValue, lang)}</Field>
          <Field label={t.premium}>{r.premium == null ? '—' : fmtKrPrice(r.premium, lang)}</Field>
          {/* The two fields the document exists for, so they are the two the eye
              lands on. Tegningskurs is pålydende + overkurs by construction, and
              is stated rather than left to be added up. */}
          <Field label={t.price}>
            <strong>{r.pricePerShare == null ? '—' : fmtKrPrice(r.pricePerShare, lang)}</strong>
          </Field>
          <Field label={t.total}>
            <strong>{r.total == null ? '—' : fmtKrExact(r.total, lang)}</strong>
          </Field>
          <Field label={t.paidDate}>
            <strong>{date(r.paidDate, lang)}</strong>
          </Field>
        </dl>
      </section>

      {(r.roundName || r.roundDate || r.resolution || r.note) && (
        <section className="kvitt__block">
          <div className="kvitt__blocktitle">{t.increase}</div>
          <dl className="kvitt__fields">
            {r.roundName && <Field label={t.round}>{r.roundName}</Field>}
            {r.roundDate && <Field label={t.roundDate}>{date(r.roundDate, lang)}</Field>}
            {r.resolution && <Field label={t.resolution}>{r.resolution}</Field>}
            {r.note && <Field label={t.note}>{r.note}</Field>}
          </dl>
        </section>
      )}

      {/* Why the holder is holding this piece of paper. One line: the price and
          the date above are their inngangsverdi, and that is the whole reason
          the document has to state them exactly. */}
      <p className="kvitt__foot">{t.foot}</p>

      <p className="kvitt__sign">
        {r.companyName || '—'} · {t.issued} {date(issuedOn, lang)}
      </p>
    </PrintSheet>
  )
}
